import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GeneralNotFound, Spinner, TaskViewer } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getTask } from 'src/api/admin/tasks';
import { getEventDocuments } from 'src/api/admin/events';

export const Route = createFileRoute('/_admin/events/$eventId/tasks/$taskId')({
	component: TaskDetailPage,
});

function TaskDetailPage() {
	const { eventId, taskId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();

	const { data: task, isLoading: taskLoading } = useQuery({
		queryKey: ['task', taskId],
		queryFn: () => getTask(taskId),
	});

	const { data: documentsData, isLoading: documentsLoading } = useQuery({
		queryKey: ['event-documents', eventId],
		queryFn: () => getEventDocuments(eventId),
	});

	const verifyPermission = authenticatedUser ? getVerifyPermission(authenticatedUser.permissions) : () => false;

	const handleClose = () => {
		navigate({ to: '/events/$eventId/tasks', params: { eventId } });
	};

	const handleOpenDocument = (documentId: string) => {
		window.open(`/events/${eventId}/documents/${documentId}`);
	};

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['task', taskId] });
		queryClient.invalidateQueries({ queryKey: ['event-tasks', eventId] });
		queryClient.invalidateQueries({ queryKey: ['event', eventId] });

		queryClient.invalidateQueries({ queryKey: ['event-documents', eventId] });
	};

	const isLoading = authLoading || taskLoading || documentsLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!task) {
		return (
			<GeneralNotFound
				label="We couldn't find this task. Maybe it has been deleted?"
				action={{
					label: 'Return to tasks',
					href: `/events/${eventId}/tasks`,
					variant: 'outlined',
				}}
			/>
		);
	}

	const documents = (documentsData?.data || []).filter((d) => task.documentIds?.includes(d._id));

	return (
		<TaskViewer
			task={task}
			documents={documents}
			onUpdate={handleUpdate}
			onClose={handleClose}
			onOpenDocument={handleOpenDocument}
			permissions={{
				taskDelete: verifyPermission(PERMISSIONS.adminEventsTasksDelete),
				taskDo: verifyPermission(PERMISSIONS.adminEventsTasksDo),
			}}
		/>
	);
}
